import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiFolder, FiPlus, FiSearch, FiShare2, FiTrash2 } from 'react-icons/fi';
import VaultNoteDetail from './vault_note_detail';
import VaultConfirmModal from './vault_confirm_modal';
import VaultCompose from './vault_compose';
import VaultHeader from './vault_header';
import ShareAccessModal from '../common/share_access_modal';
import './css/vault.css';

function buildSuggestedTags(notes) {
  const counts = new Map();
  for (const note of notes) {
    const raw = note.tags || note.tag || '';
    String(raw)
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t && t !== 'heart' && !t.startsWith('__folder_'))
      .forEach((t) => {
        const key = t.toLowerCase();
        const prev = counts.get(key);
        if (prev) prev.count += 1;
        else counts.set(key, { label: t, count: 1 });
      });
  }
  return Array.from(counts.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .map((x) => x.label)
    .slice(0, 40);
}

function notePreview(note) {
  const raw = note?.data || '';
  return String(raw)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 72);
}

function folderNamesForNote(note) {
  return String(note?.tags || '').split(',').map((tag) => tag.trim()).filter((tag) => tag.startsWith('__folder_')).map((tag) => {
    const value = tag.slice('__folder_'.length);
    try { return decodeURIComponent(value); } catch (_) { return value; }
  });
}

const VaultDesktop = ({
  documents,
  onSearchResults,
  onAddDocument,
  onUpdateNote,
  onFileUpload,
  onFileRemove,
  pendingUploads,
  selectedNoteId,
  setSelectedNoteId,
  onShareSaved,
  folders = [],
  onCreateFolder,
  onFolderShareSaved,
}) => {
  const [selectedId, setSelectedId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [query, setQuery] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [stagedFiles, setStagedFiles] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [shareFolder, setShareFolder] = useState(null);
  const isDragging = useRef(false);
  const noteListRef = useRef(null);
  const draftTitleRef = useRef(null);
  const notes = useMemo(() => documents ? Array.from(documents.values()) : [], [documents]);
  const displayFolders = useMemo(() => {
    const byOwnerAndName = new Map();
    folders.forEach((folder) => {
      const key = `${folder.owner_user_id}:${String(folder.name).toLowerCase()}`;
      byOwnerAndName.set(key, { ...folder });
    });
    notes.forEach((note) => {
      folderNamesForNote(note).forEach((name) => {
        const ownerId = note.user_id;
        const key = `${ownerId}:${name.toLowerCase()}`;
        const existing = byOwnerAndName.get(key);
        if (existing) {
          existing.item_count = (existing.item_count_seen || 0) + 1;
          existing.item_count_seen = existing.item_count;
        } else {
          byOwnerAndName.set(key, {
            id: `derived:${ownerId}:${encodeURIComponent(name.toLowerCase())}`,
            name,
            owner_user_id: ownerId,
            item_count: 1,
            can_manage_sharing: false,
            derived: true,
          });
        }
      });
    });
    return Array.from(byOwnerAndName.values()).map(({ item_count_seen, ...folder }) => folder)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, notes]);
  const selectedFolder = displayFolders.find((folder) => String(folder.id) === String(selectedFolderId));
  const visibleNotes = useMemo(() => selectedFolder
    ? notes.filter((note) => Number(note.user_id) === Number(selectedFolder.owner_user_id) && folderNamesForNote(note).some((name) => name.toLowerCase() === String(selectedFolder.name).toLowerCase()))
    : notes, [notes, selectedFolder]);
  const selectedNote = notes.find((n) => String(n.vault_id) === String(selectedId));
  const suggestedTags = useMemo(() => buildSuggestedTags(notes), [notes]);
  const canCreate = draftTitle.trim().length > 0 && !isCreating;

  const selectNoteByOffset = (delta) => {
    if (!visibleNotes.length) return;
    const currentIndex = visibleNotes.findIndex(
      (n) => String(n.vault_id) === String(selectedId)
    );
    const base = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex = Math.max(0, Math.min(visibleNotes.length - 1, base + delta));
    const nextId = visibleNotes[nextIndex]?.vault_id;
    if (nextId == null) return;
    setSelectedId(nextId);
    setSelectedNoteId?.(nextId);
  };

  useEffect(() => {
    if (isComposing) return;
    if (!visibleNotes.length) {
      setSelectedId(null);
      return;
    }
    if (selectedNoteId && visibleNotes.some((n) => String(n.vault_id) === String(selectedNoteId))) {
      setSelectedId(selectedNoteId);
      return;
    }
    if (!selectedId || !visibleNotes.some((n) => String(n.vault_id) === String(selectedId))) {
      const first = visibleNotes[0].vault_id;
      setSelectedId(first);
      setSelectedNoteId?.(first);
    }
  }, [visibleNotes, selectedNoteId, selectedId, setSelectedNoteId, isComposing]);

  useEffect(() => {
    const onDrag = (e) => {
      if (!isDragging.current) return;
      setSidebarWidth(Math.min(420, Math.max(260, e.clientX)));
    };
    const stopDrag = () => {
      isDragging.current = false;
    };
    document.addEventListener('mousemove', onDrag);
    document.addEventListener('mouseup', stopDrag);
    return () => {
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDrag);
    };
  }, []);

  // Keep the active row visible when selection changes (click or arrows).
  useEffect(() => {
    if (!selectedId || !noteListRef.current) return;
    const active = noteListRef.current.querySelector('.note-list-item.active');
    active?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  useEffect(() => {
    const shouldIgnoreArrows = (el) => {
      if (!el || !(el instanceof Element)) return false;
      // Allow arrows from the sidebar search; block while editing note/tags.
      if (el.closest('.search-bar')) return false;
      if (el.closest('.note-list')) return false;
      const tag = el.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (tag === 'INPUT') return true;
      if (el.isContentEditable) return true;
      return Boolean(el.closest('.vault-detail.is-editing'));
    };

    const onKeyDown = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (shouldIgnoreArrows(e.target)) return;
      if (!visibleNotes.length) return;

      e.preventDefault();
      selectNoteByOffset(e.key === 'ArrowDown' ? 1 : -1);
      noteListRef.current?.focus({ preventScroll: true });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visibleNotes, selectedId, setSelectedNoteId]);

  const clearStagedFiles = () => {
    stagedFiles.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    setStagedFiles([]);
  };

  const cancelCompose = () => {
    setIsComposing(false);
    setDraftTitle('');
    setDraftBody('');
    clearStagedFiles();
    setCreateError('');
    setIsCreating(false);
  };

  const startCompose = () => {
    setIsComposing(true);
    setDraftTitle('');
    setDraftBody('');
    clearStagedFiles();
    setCreateError('');
    setSelectedId(null);
    setSelectedNoteId?.(null);
    requestAnimationFrame(() => draftTitleRef.current?.focus());
  };

  const submitCompose = async () => {
    const title = draftTitle.trim();
    if (!title) {
      setCreateError('Add a title to create this note.');
      draftTitleRef.current?.focus();
      return;
    }
    setIsCreating(true);
    setCreateError('');
    try {
      const filesToUpload = stagedFiles.map((s) => s.file);
      const vaultId = await onAddDocument(title, draftBody, selectedFolder?.name || '');
      if (vaultId != null && filesToUpload.length) {
        filesToUpload.forEach((file) => onFileUpload(vaultId, file));
      }
      stagedFiles.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      setStagedFiles([]);
      setIsComposing(false);
      setDraftTitle('');
      setDraftBody('');
      setCreateError('');
      setIsCreating(false);
    } catch (err) {
      setCreateError(err?.message || 'Failed to create note.');
      setIsCreating(false);
    }
  };

  const selectNote = (vaultId) => {
    if (isComposing) cancelCompose();
    setSelectedId(vaultId);
    setSelectedNoteId?.(vaultId);
  };

  const requestDeleteNote = (vaultId, title) => {
    setDeleteTarget({ vaultId, title: title || 'Untitled' });
  };

  const cancelDeleteNote = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const confirmDeleteNote = () => {
    if (!deleteTarget || isDeleting) return;
    const { vaultId } = deleteTarget;
    const token = localStorage.getItem('walmates-token');
    const user_id = localStorage.getItem('walmates-user_id');
    setIsDeleting(true);
    axios
      .get(`/api/vault/delete?user_id=${user_id}&vault_id=${vaultId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setDeleteTarget(null);
        onSearchResults?.(query);
        if (String(selectedId) === String(vaultId)) {
          const index = notes.findIndex((n) => String(n.vault_id) === String(vaultId));
          const next = notes[index + 1] || notes[index - 1];
          selectNote(next?.vault_id || null);
        }
      })
      .catch((err) => {
        console.error('Failed to delete note', err);
        alert('Failed to delete note. Please try again.');
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  const handleSearchChange = (value) => {
    setQuery(value);
    onSearchResults?.(value);
  };

  const createFolder = async () => {
    const name = window.prompt('Folder name');
    if (!name?.trim()) return;
    try {
      const folder = await onCreateFolder?.(name.trim());
      if (folder?.id != null) setSelectedFolderId(folder.id);
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to create folder.');
    }
  };

  return (
    <div className="vault-shell">
      <VaultConfirmModal
        open={!!deleteTarget}
        title="Delete note?"
        message={
          deleteTarget
            ? localStorage.getItem('walmates-login_id') === 'walmates'
              ? `Permanently delete “${deleteTarget.title}” for everyone? This cannot be undone.`
              : `Remove “${deleteTarget.title}” from your vault? It will remain available to Walmates.`
            : ''
        }
        confirmLabel="Delete"
        busy={isDeleting}
        onConfirm={confirmDeleteNote}
        onCancel={cancelDeleteNote}
      />
      {shareFolder ? (
        <ShareAccessModal
          resource="vault"
          endpoint="/api/vault/folder-share"
          idParam="folder_id"
          itemId={shareFolder.id}
          title={shareFolder.name}
          onClose={() => setShareFolder(null)}
          onSaved={(settings) => onFolderShareSaved?.(shareFolder.id, settings)}
        />
      ) : null}
      <VaultHeader noteCount={notes.length} />
      <div
        className="split-layout"
        style={{ gridTemplateColumns: `${sidebarWidth}px 6px 1fr` }}
      >
        <aside className="sidebar" style={{ width: sidebarWidth }}>
          <button
            type="button"
            className={`vault-new-note-btn${isComposing ? ' is-active' : ''}`}
            onClick={startCompose}
          >
            <FiPlus />
            <span>New note</span>
          </button>

          <div className="search-bar">
            <FiSearch className="icon" />
            <input
              type="text"
              placeholder="Search notes…"
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                const title = e.target.value.trim();
                if (!title) return;
                const matching = notes.filter((n) =>
                  (n.title || '').toLowerCase().includes(title.toLowerCase())
                );
                if (matching.length === 0) {
                  setQuery('');
                  startCompose();
                  setDraftTitle(title);
                }
              }}
            />
          </div>

          <div className="vault-sidebar-section-title">
            <span>Folders</span>
            <button type="button" onClick={createFolder} aria-label="New folder" title="New folder"><FiPlus /></button>
          </div>
          <div className="vault-folder-list">
            <button
              type="button"
              className={`vault-folder-row${selectedFolderId == null ? ' is-active' : ''}`}
              onClick={() => setSelectedFolderId(null)}
            >
              <FiFolder /><span>All items</span><small>{notes.length}</small>
            </button>
            {displayFolders.map((folder) => (
              <div className={`vault-folder-row${String(selectedFolderId) === String(folder.id) ? ' is-active' : ''}`} key={folder.id}>
                <button type="button" className="vault-folder-select" onClick={() => setSelectedFolderId(folder.id)}>
                  <FiFolder /><span>{folder.name}</span><small>{folder.item_count ?? 0}</small>
                </button>
                {folder.can_manage_sharing ? (
                  <button type="button" className="vault-folder-share" onClick={() => setShareFolder(folder)} title={`Share ${folder.name}`} aria-label={`Share ${folder.name}`}><FiShare2 /></button>
                ) : null}
              </div>
            ))}
          </div>

          <div className="vault-sidebar-section-title"><span>Items{selectedFolder ? ` · ${selectedFolder.name}` : ''}</span></div>

          <div
            className="note-list"
            ref={noteListRef}
            tabIndex={0}
            role="listbox"
            aria-label="Vault notes"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                selectNoteByOffset(1);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                selectNoteByOffset(-1);
              }
            }}
          >
            {visibleNotes.length === 0 ? (
              <div className="note-list-empty">
                No notes yet. Create one to get started.
              </div>
            ) : (
              visibleNotes.map((n, index) => {
                const active = String(n.vault_id) === String(selectedId);
                const preview = notePreview(n);
                return (
                  <div
                    key={n.vault_id}
                    role="option"
                    aria-selected={active}
                    className={`note-list-item${active ? ' active' : ''}`}
                    onClick={() => selectNote(n.vault_id)}
                  >
                    <span className="note-list-serial" aria-hidden="true">
                      {index + 1}
                    </span>
                    <div className="note-list-item-main">
                      <div className="note-list-item-title">
                        {n.title || 'Untitled'}
                      </div>
                      {preview ? (
                        <div className="note-list-item-preview">{preview}</div>
                      ) : null}
                    </div>
                    {active && n.can_manage_sharing ? (
                      <button
                        type="button"
                        className="note-list-delete"
                        title="Delete note"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteNote(n.vault_id, n.title);
                        }}
                      >
                        <FiTrash2 />
                      </button>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </aside>

        <div
          className="vault-splitter"
          onMouseDown={() => {
            isDragging.current = true;
          }}
          role="separator"
          aria-orientation="vertical"
        />

        <main className="main-panel">
          {isComposing ? (
            <VaultCompose
              title={draftTitle}
              body={draftBody}
              onTitleChange={(v) => {
                setDraftTitle(v);
                if (createError) setCreateError('');
              }}
              onBodyChange={setDraftBody}
              stagedFiles={stagedFiles}
              onStagedFilesChange={setStagedFiles}
              onCancel={cancelCompose}
              onSubmit={submitCompose}
              isCreating={isCreating}
              createError={createError}
              canCreate={canCreate}
              titleRef={draftTitleRef}
            />
          ) : selectedNote ? (
            <VaultNoteDetail
              note={selectedNote}
              onUpdateNote={onUpdateNote}
              onFileUpload={onFileUpload}
              onFileRemove={onFileRemove}
              pendingUploads={pendingUploads}
              suggestedTags={suggestedTags}
              onShareSaved={(settings) => onShareSaved?.(selectedNote.vault_id, settings)}
            />
          ) : (
            <div className="vault-empty-state">
              <h2>Your private vault</h2>
              <p>Select a note from the left, or create a new one.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default VaultDesktop;
